import type { InfiniteData, QueryClient } from '@tanstack/react-query';
import { queryKeys } from '@/lib/query-keys';
import type { ListaMensajes, MensajeChat } from '@/types/conversations';

/**
 * Caché del inbox después de responder a mano (texto o foto).
 *
 * Vive aparte de los hooks porque las dos respuestas (`useSendMessage` y `useSendImage`) hacen
 * exactamente lo mismo con la caché, y tenerlo dos veces sería tener dos maneras de que el hilo no
 * cuadre. Las dos reglas:
 *
 *  1. **el saliente que devuelve el backend se añade al hilo en caché** (`pages[0]`, la página más
 *     nueva; el `siguienteCursor` de esa página no cambia porque apunta a su mensaje más antiguo), en
 *     vez de rehacer la petición: el usuario ve su mensaje al instante;
 *  2. **el listado y el detalle se invalidan**, porque los dos muestran «lo último que pasó» y el
 *     `mensajesTotal`, y eso acaba de cambiar.
 */

/** Añade el mensaje a la página más nueva del historial (`pages[0]`). */
export function appendToNewestPage(
  paginas: ListaMensajes[],
  mensaje: MensajeChat,
): ListaMensajes[] {
  const [nueva, ...anteriores] = paginas;
  if (!nueva) return paginas;

  return [
    { ...nueva, mensajes: [...nueva.mensajes, mensaje], total: nueva.total + 1 },
    ...anteriores,
  ];
}

/** Mete el saliente recién creado en el hilo que ya está pintado (si hay historial en caché). */
export function patchMessagesCache(
  queryClient: QueryClient,
  tenantId: string,
  conversationId: string,
  mensaje: MensajeChat,
): void {
  queryClient.setQueryData<InfiniteData<ListaMensajes>>(
    queryKeys.conversations.messages(tenantId, conversationId),
    (data) => (data ? { ...data, pages: appendToNewestPage(data.pages, mensaje) } : data),
  );
}

/** Invalida lo que muestra «lo último que pasó» y los contadores, tras un envío correcto. */
export function invalidateAfterSend(
  queryClient: QueryClient,
  tenantId: string,
  conversationId: string,
): void {
  queryClient.invalidateQueries({ queryKey: queryKeys.conversations.lists(tenantId) });
  queryClient.invalidateQueries({
    queryKey: queryKeys.conversations.detail(tenantId, conversationId),
  });
}

/**
 * Reemplaza un mensaje ya pintado por su versión actualizada (mismo `id`).
 *
 * Lo usa el REENVÍO: el mensaje no es nuevo (no se añade al final ni cambia `lastMessageAt`), solo
 * cambia su desenlace (`entregado`/`errorEnvio`), así que se sustituye en todas las páginas en las
 * que aparezca por si el asesor subió a leer historial antes de reintentar.
 */
export function replaceMessageInCache(
  queryClient: QueryClient,
  tenantId: string,
  conversationId: string,
  mensaje: MensajeChat,
): void {
  queryClient.setQueryData<InfiniteData<ListaMensajes>>(
    queryKeys.conversations.messages(tenantId, conversationId),
    (data) =>
      data
        ? {
            ...data,
            pages: data.pages.map((pagina) => ({
              ...pagina,
              mensajes: pagina.mensajes.map((m) => (m.id === mensaje.id ? mensaje : m)),
            })),
          }
        : data,
  );
}